import { type ReactNode, useEffect, useState } from "react";
import type { SessionInfo } from "../api";
import { Header } from "./Header";
import type { Page } from "./router";
import { Sidebar } from "./Sidebar";
import { closeDrawer } from "./theme";

declare global {
  interface Window {
    HSStaticMethods?: { autoInit: (collection?: string | string[]) => void };
  }
}

/**
 * Preline drives the template's dropdowns, modals and overlays. As in the template's own
 * PrelineScript, it is loaded once and asked to wire up again whenever the page changes,
 * because a dropdown rendered after the last wiring would otherwise not open.
 */
export function usePreline(page: string): void {
  useEffect(() => {
    let live = true;
    void import("preline/preline").then(() => {
      if (live) window.HSStaticMethods?.autoInit();
    });
    return () => {
      live = false;
    };
  }, [page]);
}

/** The template's page shell: header, sidebar, content, footer, back to top. */
export function ContentLayout({
  session,
  page,
  onSignOut,
  children,
}: {
  readonly session: SessionInfo;
  readonly page: Page;
  readonly onSignOut: () => void;
  readonly children: ReactNode;
}): React.ReactElement {
  usePreline(page);

  return (
    <div className="page">
      <Header session={session} onSignOut={onSignOut} />
      <Sidebar page={page} role={session.user.role} />
      <div className="content">
        <div className="main-content" onClick={() => closeDrawer()}>
          {children}
        </div>
      </div>
      <Footer />
      <BackToTop />
    </div>
  );
}

/**
 * The template's page title row, in the form its dashboard uses: a title and a line of
 * explanation on the left, the page's own actions on the right.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly actions?: ReactNode;
}): React.ReactElement {
  return (
    <div className="md:flex block items-center justify-between gap-4 my-[1.5rem] page-header-breadcrumb">
      <div className="min-w-0">
        <p className="font-semibold text-[1.125rem] text-defaulttextcolor dark:text-defaulttextcolor/70 !mb-0 ">
          {title}
        </p>
        {subtitle ? (
          <p className="font-normal text-[#8c9097] dark:text-white/50 text-[0.813rem]">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="btn-list shrink-0 md:mt-0 mt-2">{actions}</div> : null}
    </div>
  );
}

function Footer(): React.ReactElement {
  return (
    <footer className="footer mt-auto xl:ps-[15rem]  font-normal font-inter bg-white text-defaultsize leading-normal text-[0.813] shadow-[0_0_0.4rem_rgba(0,0,0,0.1)] dark:bg-bodybg py-4 text-center">
      <div className="container">
        <span className="text-gray dark:text-defaulttextcolor/50">
          Copyright © {new Date().getFullYear()}{" "}
          <span className="text-defaulttextcolor font-semibold dark:text-defaulttextcolor">
            Vanguard Elite Holdings Pte Ltd
          </span>
          . Point of sale, inventory and accounting.
        </span>
      </div>
    </footer>
  );
}

function BackToTop(): React.ReactElement {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const onScroll = (): void => setShown(window.scrollY > 100);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="scrollToTop" style={{ display: shown ? "flex" : "none" }} onClick={() => window.scrollTo(0, 0)}>
      <span className="arrow">
        <i className="ri-arrow-up-s-fill text-xl" />
      </span>
    </div>
  );
}
